// ============================================================
// Field.js
// ------------------------------------------------------------
// แถวฟอร์มมาตรฐานของระบบ: ป้ายชื่อกว้างคงที่ทางซ้าย + ช่องกรอกทางขวา
// เดิม markup ชุดนี้ copy อยู่ 15 หน้า และบางหน้าทำเป็น helper ที่รับพารามิเตอร์
// เรียงตำแหน่ง 8 ตัว (label, field, isReq, isNum, isFloat, max, readOnly, cls)
// ซึ่งอ่านไม่ออกว่าตัวไหนคืออะไร — เปลี่ยนมาส่งเป็นชื่อ prop แทน
//
// ใช้:
//   <TextField label="ชื่อ-นามสกุล" req value={x} onChange={(v) => ...} />
//   <TextField label="เลขบัตรประชาชน" numeric maxLength={13} ... />
//   <TextField label="วงเงินกู้" decimal ... />
//   <DateField label="วันที่โอน" value={x} onChange={(v) => ...} />
//   <SelectField label="สถานะการโอน" options={OPTS} value={x} onChange={(v) => ...} />
// ============================================================

import React from "react";
import ReactSelect from "react-select";

import DatePicker from "./DatePicker.js";
import { select_style } from "./Select.js";
import { required } from "../core/validate.js";
import { number as onlyNumber, float as isFloat } from "../core/format.js";

// โครงแถวฟอร์ม — ป้ายชื่อ + ช่องกรอก
// labelWidth ปรับได้เผื่อหน้าจอที่ป้ายยาวกว่าปกติ (ค่าเริ่มต้น wpx-140 ตามดีไซน์เดิม)
export function FormRow({ label, req, children, labelWidth = "wpx-140", className = "w-100 d-flex align-items-center mb-3" }) {
  return (
    <div className={className}>
      <label className={labelWidth}>
        {label}
        {req && required} :
      </label>
      {children}
    </div>
  );
}

// ช่องกรอกข้อความ
//   numeric  = รับเฉพาะตัวเลข (ตัดอักขระอื่นทิ้ง) ใช้กับเบอร์โทร/เลขบัตร
//   decimal  = รับตัวเลขทศนิยม ถ้าพิมพ์เกินรูปแบบที่รับได้จะไม่รับค่า
//   onChange ได้ค่าเป็นสตริงที่ผ่านการกรองแล้ว
export function TextField({ label, req, value, onChange, numeric, decimal, maxLength, readOnly = false, placeholder = "กรอกข้อมูล", className = "form-control border-0 bg-contain validate", labelWidth, rowClassName }) {
  return (
    <FormRow label={label} req={req} labelWidth={labelWidth} className={rowClassName}>
      <input
        className={className}
        type={numeric ? "tel" : "text"}
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        readOnly={readOnly}
        onChange={(e) => {
          let v = e.target.value;
          if (numeric) v = onlyNumber(v);
          if (decimal && !isFloat(v)) return; // พิมพ์ทศนิยมเกินที่กำหนด → ไม่รับค่า
          onChange(v);
        }}
      />
    </FormRow>
  );
}

// ช่องเลือกวันที่ (ปฏิทิน พ.ศ.) — onChange ได้ "YYYY-MM-DD" (ค.ศ.)
export function DateField({ label, req, value, onChange, disabled, min, max, position, labelWidth, rowClassName, className = "form-control bg-contain border-0 w-100" }) {
  return (
    <FormRow label={label} req={req} labelWidth={labelWidth} className={rowClassName}>
      <DatePicker className={className} value={value} onChange={onChange} disabled={disabled} min={min} max={max} position={position} />
    </FormRow>
  );
}

// ช่องเลือกตัวเลือก (react-select) — onChange ได้ทั้ง option object เหมือนเดิม
export function SelectField({ label, req, value, onChange, options, isClearable, isDisabled, labelWidth, rowClassName, className = "form-control bg-contain border-0 select-search" }) {
  return (
    <FormRow label={label} req={req} labelWidth={labelWidth} className={rowClassName}>
      <ReactSelect className={className} styles={select_style} options={options} value={value} onChange={onChange} isClearable={isClearable} isDisabled={isDisabled} />
    </FormRow>
  );
}
